import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Radio,
  Typography,
} from '@mui/material';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';
import { Dispatch, SetStateAction, useCallback, useState } from 'react';
import { styled } from '@mui/material/styles';
import ErrorWarningFillIcon from 'remixicon-react/ErrorWarningFillIcon';
import { theme } from 'config/theme';
import { ModeratedContentData, PostFormData } from '@buildwise/pages/addPost/hooks/useAddPostForm';
import { useFormContext, useWatch } from 'react-hook-form';
import { extractPlainText } from 'utility/helpers';

type PostContentModerationModalProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  moderatedContent: ModeratedContentData;
  setModeratedContent: React.Dispatch<React.SetStateAction<ModeratedContentData>>;
  onSubmit: () => void;
};

function PostContentModerationModal({
  open,
  moderatedContent,
  setOpen,
  setModeratedContent,
  onSubmit,
}: PostContentModerationModalProps) {
  const closeModal = () => {
    setOpen(false);
  };
  const {
    control,
    setValue,
    formState: { isSubmitting },
  } = useFormContext<PostFormData>();

  const [selectedValue, setSelectedValue] = useState('modified');
  const title = useWatch({
    control,
    name: 'title',
    defaultValue: '',
  });
  const description = useWatch({
    control,
    name: 'description',
    defaultValue: '',
  });

  const [originalTitle, setOriginalTitle] = useState(null);
  const [originalDescription, setOriginalDescription] = useState(null);

  const handleChange = (event) => {
    setSelectedValue(event.target.value);
  };

  const handelApply = useCallback(() => {
    if (selectedValue === 'modified') {
      setOriginalTitle(title);
      setOriginalDescription(description);
      setValue(
        'title',
        moderatedContent?.ai_generated_response?.title
          ? Array.isArray(moderatedContent.ai_generated_response.title)
            ? moderatedContent.ai_generated_response.title[0]
            : moderatedContent.ai_generated_response.title
          : '',
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
      setValue(
        'description',
        `<p>${moderatedContent?.ai_generated_response?.description ? (Array.isArray(moderatedContent.ai_generated_response.description) ? moderatedContent.ai_generated_response.description[0] : moderatedContent.ai_generated_response.description) : ''}</p>`,
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
    }
    onSubmit();
  }, [closeModal, moderatedContent, setValue, setModeratedContent, selectedValue, onSubmit]);

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth={'md'}
      sx={{
        WebkitOverflowScrolling: 'touch',
        '& .MuiPaper-root': {
          borderRadius: '8px',
          minHeight: '60%',
          transition: 'all 0.2s',
          height: '90%',
        },
      }}
    >
      <CustomDialogTitle onClose={closeModal} id="dialog-title">
        <Box sx={{ display: 'flex', alignItems: 'center', ml: -1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <ErrorWarningFillIcon size={26} color={theme.palette.custom.coolGrey} />
          </Box>
          <Title>Select the version you'd like to post</Title>
        </Box>
      </CustomDialogTitle>
      <DialogContent
        id="dialog-description"
        dividers
        sx={{ p: 2, borderBottom: 'none', backgroundColor: '#f8f7fc' }}
      >
        {/* <Box sx={{ color: theme.palette.custom.tuna }}>
          Your post has been modified based on company guidelines. Select the version that you would
          like to keep when publishing.
        </Box> */}
        <Box sx={{ backgroundColor: theme.palette.custom.white, borderRadius: 2, pl: 2, pt: 1 }}>
          <FormControlLabel
            value="original"
            control={<Radio checked={selectedValue === 'original'} onChange={handleChange} />}
            label={
              <Box sx={{ mt: 1 }}>
                <Typography
                  sx={{
                    fontWeight: '700 !important',
                    fontSize: '14px !important',
                    color: `${theme.palette.custom.tuna} !important`,
                  }}
                >
                  Original Version
                </Typography>
                <Box
                  sx={{
                    ml: 2,
                    mt: 2,
                  }}
                >
                  <Typography
                    sx={{
                      mt: 1,
                      fontWeight: '700 !important',
                      fontSize: '18px !important',
                      color: `${theme.palette.custom.tuna} !important`,
                    }}
                  >
                    {originalTitle || title}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1,
                      fontWeight: '400 !important',
                      color: `${theme.palette.custom.black} !important`,
                    }}
                    component="div"
                    dangerouslySetInnerHTML={{
                      __html: originalDescription || description,
                    }}
                  />
                </Box>
              </Box>
            }
            sx={{ alignItems: 'flex-start', width: '100%', mb: 2 }}
          />
        </Box>
        <Box
          sx={{ backgroundColor: theme.palette.custom.white, borderRadius: 2, pl: 2, pt: 1, mt: 2 }}
        >
          <FormControlLabel
            value="modified"
            control={<Radio checked={selectedValue === 'modified'} onChange={handleChange} />}
            label={
              <Box sx={{ mt: 1 }}>
                <Typography
                  sx={{
                    fontWeight: '700 !important',
                    fontSize: '14px !important',
                    color: `${theme.palette.custom.tuna} !important`,
                  }}
                >
                  Modified Version With Suggested Changes
                </Typography>
                <Box
                  sx={{
                    ml: 2,
                    mt: 2,
                  }}
                >
                  <Typography
                    sx={{
                      mt: 1,
                      fontWeight: '700 !important',
                      fontSize: '18px !important',
                      color: `${theme.palette.custom.tuna} !important`,
                    }}
                  >
                    {moderatedContent?.ai_generated_response?.title || ''}
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1,
                      fontWeight: '400 !important',
                      color: `${theme.palette.custom.black} !important`,
                    }}
                    component="div"
                    dangerouslySetInnerHTML={{
                      __html: moderatedContent?.ai_generated_response?.description || '',
                    }}
                  />
                </Box>
              </Box>
            }
            sx={{ alignItems: 'flex-start', width: '100%', mb: 2 }}
          />
        </Box>
        <Box sx={{ my: 2, mx: 8 }}>
          <Typography sx={{ fontWeight: 700, color: theme.palette.custom.tuna }}>
            Changes Made
          </Typography>
          {moderatedContent?.ai_generated_response?.modifications?.map((mod) => (
            <Box
              sx={{
                p: 1,
                mt: 1,
                borderLeft: `4px solid ${theme.palette.custom.link}`,
                backgroundColor: theme.palette.custom.aliceBlue,
              }}
            >
              {!!mod?.modified ? (
                <>
                  <del style={{ color: 'red', marginRight: 4 }}>
                    {extractPlainText(mod?.original) || ''}
                  </del>
                  <strong style={{ marginRight: '4px' }}>is modified to</strong>
                  {extractPlainText(mod?.modified) || ''}
                </>
              ) : (
                <>
                  {extractPlainText(mod?.original) || ''}
                  <strong>is removed</strong>
                </>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Box
          sx={{
            display: 'flex',
            width: '100%',
          }}
        >
          <Box sx={{ ml: 'auto' }}>
            <Button variant="outlined" onClick={closeModal} sx={{ mr: 1 }} disabled={isSubmitting}>
              Go Back
            </Button>
            <Button variant="contained" onClick={handelApply} disabled={isSubmitting}>
              {isSubmitting ? (
                <CircularProgress
                  sx={(theme) => ({ color: theme.palette.custom.black })}
                  size={20}
                />
              ) : (
                'Save'
              )}
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

const Title = styled(Typography, { label: 'Title' })(({ theme }) => ({
  fontSize: 20,
  fontWeight: 700,
  color: theme.palette.custom.tuna,
  marginLeft: 8,
}));

export default PostContentModerationModal;
